import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle } from "lucide-react";
import { Reveal, SectionHead } from "@/lib/motion";
import { BUSINESS_SERVICES, INDIVIDUAL_ONLY_NOTE_POSITIVE, INDIVIDUAL_ONLY_NOTE_NEGATIVE } from "@/data/content";

export default function BusinessServices() {
  return (
    <div data-testid="business-services-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="Business Services"
            title="For sole owners who want straight numbers."
            copy="Tax preparation and bookkeeping for sole proprietors and single-member LLCs, with the same standard as everything we do: accurate, explained, and reviewed with you before anything is filed."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <Reveal>
          <div
            className="mb-12 flex items-start gap-3 border-2 border-red-500/60 bg-red-950/30 p-5"
            data-testid="individual-only-note"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
            <p className="text-sm leading-relaxed">
              <span className="text-emerald-400">{INDIVIDUAL_ONLY_NOTE_POSITIVE}</span>{" "}
              <span className="text-red-400">{INDIVIDUAL_ONLY_NOTE_NEGATIVE}</span>
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {BUSINESS_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07}>
              <Link
                to={s.to}
                data-testid={`business-service-card-${i}`}
                className="group flex h-full flex-col border border-steel-border bg-steel-surface p-10 transition-colors duration-300 hover:border-steel-brass/60 hover:bg-steel-elevated"
              >
                <span className="font-mono text-sm text-steel-brass">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-display text-2xl font-medium text-steel-text">{s.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-steel-secondary sm:text-base">{s.desc}</p>
                <span className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel-brass transition-colors duration-300 group-hover:text-steel-text">
                  {s.cta}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-start justify-between gap-8 border border-steel-border bg-steel-surface/60 p-10 lg:flex-row lg:items-center">
            <div>
              <h3 className="font-display text-2xl font-medium text-steel-text">Same process. Same standard.</h3>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-steel-secondary">
                Business engagements follow the same eight-step process as individual returns — including a
                full review with you before filing. See exactly what happens, in what order.
              </p>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Link
                to="/how-it-works"
                data-testid="business-how-it-works-btn"
                className="flex items-center justify-center gap-2 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-bg"
              >
                How It Works
              </Link>
              <Link
                to="/get-started"
                data-testid="business-start-btn"
                className="group flex items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover"
              >
                Talk to Us
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
